// The ONE artifact/file type badge (#11548 r68, #12481): every surface that
// shows a type chip — the Artifacts library, the agent page's Artifacts
// tab, the run panel's Outputs tree, the artifact pane header, the card in
// the thread — renders THIS vocabulary and THIS palette, so "TBL" never sits
// beside "TABLE" and green never means two different things.
//
// A kind is a category, so its colour comes from the design tokens' tag ramp
// (`--fp-tag-*`, RMS/design/tokens.css): documents blue, spreadsheets and
// tables green, decks orange, PDF and images red, diagrams teal, markdown
// and text slate, code purple. Status colour means judgement and is never
// used here.
import React from "react";

export type BadgeHue =
  | "blue"
  | "green"
  | "orange"
  | "red"
  | "teal"
  | "slate"
  | "purple";

const HUE_BY_EXT: Record<string, BadgeHue> = {
  xlsx: "green",
  xls: "green",
  csv: "green",
  json: "green",
  tbl: "green",
  pptx: "orange",
  ppt: "orange",
  docx: "blue",
  doc: "blue",
  pdf: "red",
  png: "red",
  jpg: "red",
  jpeg: "red",
  gif: "red",
  svg: "red",
  dia: "teal",
  md: "slate",
  txt: "slate",
  py: "purple",
  ts: "purple",
  tsx: "purple",
  js: "purple",
  jsx: "purple",
};

/** The tag hue for an extension or registry short name, in any case; slate
 *  for one the ramp does not know. */
export const badgeHueFor = (ext: string): BadgeHue =>
  HUE_BY_EXT[ext.toLowerCase()] ?? "slate";

/** The extension of a file name, lower-cased: a short alphanumeric suffix
 *  after the last dot, so "Q3 v2.1 report" has none and "memo.DOCX" has
 *  "docx". Null when the name carries none. */
export const fileExtension = (name: string): string | null => {
  const match = /\.([a-z0-9]{1,5})$/i.exec(name.trim());
  return match ? match[1].toLowerCase() : null;
};

export const KIND_BY_EXT: Record<string, string> = {
  xlsx: "Spreadsheet",
  xls: "Spreadsheet",
  csv: "Spreadsheet",
  pptx: "Deck",
  ppt: "Deck",
  docx: "Document",
  doc: "Document",
  pdf: "PDF",
  // Literal, recognizable names (r70b): an MD badge beside a "Note"
  // type read as a mismatch.
  md: "Markdown",
  markdown: "Markdown",
  txt: "Text",
  tbl: "Table",
  dia: "Diagram",
};

/** The kind word for an extension or short name: the shared vocabulary, or
 *  "File" for one it does not know. */
export const kindForExt = (ext: string): string =>
  KIND_BY_EXT[ext.toLowerCase()] ?? "File";

/** The badge extension, kind label, and display title for one artifact —
 *  files show their extension as the badge and drop it from the title;
 *  registry kinds wear their short names (TBL / DIA / DOC). */
export const displayFacts = (artifact: {
  kind: string;
  filename: string | null;
  title: string;
  workspace_id?: string | null;
  content?: string | null;
}): { ext: string; kind: string; title: string } => {
  if (artifact.kind === "diagram") {
    return { ext: "DIA", kind: "Diagram", title: artifact.title };
  }
  if (artifact.kind === "table") {
    return { ext: "TBL", kind: "Table", title: artifact.title };
  }
  if (artifact.workspace_id) {
    // Workspace-backed rows are Documents (round 14) — the editor formerly
    // branded Report is the generic document editor now.
    return { ext: "DOC", kind: "Document", title: artifact.title };
  }
  if ((artifact.content ?? null) !== null && !artifact.filename) {
    // Content-backed documents keep their title verbatim — ext-splitting a
    // dotted title ("… Call No. 12") minted a bogus "12" badge (r14b).
    return { ext: "DOC", kind: "Document", title: artifact.title };
  }
  const name = (artifact.filename ?? artifact.title).replace(/\.b64$/i, "");
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase() : "doc";
  const stripped =
    artifact.title === name && dot > 0 ? name.slice(0, dot) : artifact.title;
  return {
    ext: ext.toUpperCase(),
    kind: kindForExt(ext),
    title: stripped,
  };
};

/** The tile's three sizes: inline beside a title (the pane header, a list
 *  row), the card's square (the thread, the library grid), and the stack in
 *  an answer's sources chip. */
export type TileSize = "sm" | "lg" | "xl" | "xs";

const TILE_SIZE: Record<TileSize, React.CSSProperties> = {
  xs: { height: 18, minWidth: 18, padding: "0 3px", fontSize: 8 },
  sm: { height: 20, minWidth: 34, padding: "0 5px", fontSize: 9.5 },
  lg: {
    height: 36,
    width: 36,
    padding: 0,
    fontSize: 10,
    borderRadius: 8,
  },
  // The thread card's square (#12481): the card's whole left edge.
  xl: {
    height: 56,
    width: 56,
    padding: 0,
    fontSize: 11,
    borderRadius: 0,
  },
};

/**
 * The type tile itself (#12481): the extension or short name in the mono
 * face on its hue's wash, named for a screen reader by its kind. `TypeBadge`
 * and `ListBadge` below are the sizes the lists already wore; the pane
 * header and the thread card take `lg` / `sm` directly.
 */
export const TypeTile = ({
  ext,
  kind,
  size = "sm",
}: {
  ext: string;
  /** The full type word the abbreviation stands for; the accessible name. */
  kind?: string;
  size?: TileSize;
}) => {
  const hue = badgeHueFor(ext);
  const label = kind ?? kindForExt(ext);
  return (
    <span
      role="img"
      aria-label={label}
      data-hue={hue}
      className="inline-flex flex-none items-center justify-center rounded-[5px] font-mono font-semibold uppercase tracking-[0.04em]"
      style={{
        backgroundColor: `var(--fp-tag-${hue}-bg)`,
        color: `var(--fp-tag-${hue})`,
        // The card-edge square draws no border of its own: the card's
        // edge and its own hairline divider frame it.
        border:
          size === "xl"
            ? "none"
            : `1px solid color-mix(in srgb, var(--fp-tag-${hue}) 25%, transparent)`,
        ...TILE_SIZE[size],
      }}
    >
      {ext.toUpperCase()}
    </span>
  );
};

/** The compact chip: pass the raw name (a filename — colored by its
 *  extension) or an artifact's facts.ext (TBL / DIA / DOC). */
export const TypeBadge = ({ text }: { text: string }) => {
  const hue = badgeHueFor(text);
  return (
    <span
      data-hue={hue}
      className="flex-none rounded px-1 font-mono text-[8.5px] font-semibold uppercase"
      style={{
        backgroundColor: `var(--fp-tag-${hue}-bg)`,
        color: `var(--fp-tag-${hue})`,
      }}
    >
      {text}
    </span>
  );
};

/** Below this frame width an artifact list compacts to name + badge: the
 *  Type (and Modified / Records) columns go and the badge carries the full
 *  type word as its accessible name (CD, #11742 round 7). The line sits
 *  where a list's full grid stops leaving the name a usable track — the
 *  agent and record tabs' grids are 96 + 150px of fixed columns, so 560;
 *  the library's columns resize, so it computes its own line from their
 *  widths (`compactBelow` in ArtifactsPage). */
export const COMPACT_LIST_BELOW_PX = 560;

/** The library list's larger chip (r70): the exact badge the main
 *  Artifacts tab renders beside each name, shared so the agent tab and
 *  the record tab wear the identical one. */
export const ListBadge = ({
  ext,
  label,
}: {
  ext: string;
  /**
   * The full type word the abbreviation stands for. Where a compacted list
   * drops its Type column, the badge is the one rendering of the type left
   * on the row, so it carries the full word as its accessible name
   * (#11742 round 7, CD).
   */
  label?: string;
}) => {
  const hue = badgeHueFor(ext);
  return (
    <span
      role={label ? "img" : undefined}
      aria-label={label}
      data-hue={hue}
      className="inline-flex h-5 min-w-[34px] flex-none items-center justify-center rounded-[5px] px-1.5 font-mono text-[10px] font-medium tracking-[0.06em]"
      style={{
        backgroundColor: `var(--fp-tag-${hue}-bg)`,
        color: `var(--fp-tag-${hue})`,
      }}
    >
      {ext}
    </span>
  );
};

/** One artifact's badge, whatever surface it lists on. */
export const ArtifactBadge = ({
  artifact,
}: {
  artifact: {
    kind: string;
    filename: string | null;
    title: string;
    workspace_id?: string | null;
    content?: string | null;
  };
}) => <TypeBadge text={displayFacts(artifact).ext} />;
